import { useState, useEffect, useCallback } from 'react';
import { getValidToken } from '../utils/auth';
import TradingDatePicker from './TradingDatePicker';
import { etTodayStr } from '../utils/tradingCalendar';
import { optionTradingViewUrl } from '../utils/tradingViewLink';

const API_URL = process.env.REACT_APP_API_URL || '';

const STATUS_COLOR = {
  EntryPending: '#f59e0b', Holding: '#60a5fa', Success: '#22c55e',
  Closed: '#9ca3af', Unfilled: '#6b7280', Orphaned: '#ef4444',
};

function formatTime(unixMs) {
  if (!unixMs) return '—';
  return new Date(unixMs).toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'America/New_York',
  });
}

function money(v, digits = 2) {
  return v > 0 ? `$${v.toFixed(digits)}` : '—';
}

// One instance per bot variant: the SPY paper-trading bot and the SPX
// watch-only (simulated fills) variant. Same payload shape from both routes.
export default function CheapBotWidget({ apiPath, title, subtitle }) {
  const [date, setDate] = useState(() => etTodayStr());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (d) => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}${apiPath}?date=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [apiPath]);

  useEffect(() => {
    fetchData(date);
    const timer = setInterval(() => fetchData(date), 5000);
    return () => clearInterval(timer);
  }, [date, fetchData]);

  const trades = data?.trades || [];
  const realized = trades.filter((t) => t.entry_price > 0 && t.exit_price > 0);
  const wins = realized.filter((t) => t.exit_price > t.entry_price).length;
  const pnlDollars = realized.reduce(
    (sum, t) => sum + (t.exit_price - t.entry_price) * 100 * t.qty, 0);

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <span className="text-xs font-bold px-3 py-1.5 rounded border"
          style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}>
          {title}
        </span>
        {data && (
          <span className="text-xs px-2 py-1 rounded font-semibold"
            style={{ backgroundColor: '#1f2937', color: '#93c5fd' }}>
            {data.state}
          </span>
        )}
        {data?.simulated && (
          <span className="text-xs px-2 py-1 rounded font-semibold"
            style={{ backgroundColor: '#f59e0b22', color: '#f59e0b' }}>
            SIMULATED FILLS
          </span>
        )}
        {data?.spot > 0 && (
          <span className="text-xs text-gray-500 font-mono">{data.underlying} ~{data.spot.toFixed(2)}</span>
        )}
        <div className="flex-1" />
        {realized.length > 0 && (
          <span className="text-xs font-semibold font-mono"
            style={{ color: pnlDollars >= 0 ? '#22c55e' : '#ef4444' }}>
            {wins}/{realized.length} wins · {pnlDollars >= 0 ? '+' : ''}${pnlDollars.toFixed(0)}
          </span>
        )}
        <TradingDatePicker value={date} onChange={setDate} />
      </div>
      <div className="text-xs text-gray-500 mb-3">{subtitle}</div>
      {error && <div className="text-red-400 text-sm mb-3">Failed to load: {error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr className="text-gray-500 border-b" style={{ borderColor: '#1f2937' }}>
              {['Type', 'Strike', 'SessHigh', 'Low', 'Trigger', 'Entry', 'TP', 'Exit', 'Why', 'Qty', 'H', 'L', 'PostPeak', 'P&L%', 'Entry Time', 'Exit Time', 'Status'].map((h) => (
                <th key={h} className={`py-2 pr-3 font-medium ${h === 'Type' ? 'text-left' : 'text-right'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr><td colSpan={17} className="py-4 text-center text-gray-600">No trades</td></tr>
            ) : trades.map((t, i) => {
              const pnl = t.entry_price > 0 && t.exit_price > 0
                ? ((t.exit_price - t.entry_price) / t.entry_price) * 100 : null;
              return (
                <tr key={`${t.symbol}-${t.created_at}-${i}`}
                  className="border-b hover:bg-gray-800/50" style={{ borderColor: '#1f2937' }}>
                  <td className="py-1.5 pr-3">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: (t.type === 'C' ? '#22c55e' : '#ef4444') + '22', color: t.type === 'C' ? '#22c55e' : '#ef4444' }}>
                      {t.type === 'C' ? 'CALL' : 'PUT'}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono">
                    <a href={optionTradingViewUrl(t.symbol)} target="_blank" rel="noopener noreferrer"
                      title="Open on TradingView"
                      style={{ color: '#60a5fa', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                      {t.strike % 1 === 0 ? t.strike.toFixed(0) : t.strike.toFixed(1)}
                    </a>
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{money(t.session_high)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{money(t.local_low)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{money(t.trigger_price)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{money(t.entry_price)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono" style={{ color: '#22c55e' }}>{money(t.tp_price)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-300">{money(t.exit_price)}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{t.exit_reason || '—'}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{t.qty}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{money(t.high)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-gray-400">{money(t.low)}</td>
                  <td className="py-1.5 pr-3 text-right font-mono"
                    title="Highest bid seen after our exit (what the fixed +100% TP left on the table)"
                    style={{ color: t.post_exit_peak > t.exit_price ? '#f59e0b' : '#4b5563' }}>
                    {money(t.post_exit_peak)}
                  </td>
                  {pnl == null ? (
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-500">—</td>
                  ) : (
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold"
                      style={{ color: pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                      {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)}%
                    </td>
                  )}
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.entry_at || t.created_at)}</td>
                  <td className="py-1.5 pr-3 text-right text-gray-400">{formatTime(t.exit_at)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    <span className="font-semibold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: (STATUS_COLOR[t.status] || '#6b7280') + '22', color: STATUS_COLOR[t.status] || '#6b7280' }}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
